/**
 * ReceptionSummaryCards Component
 * Displays summary stats and status chips in a compact single-row layout
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  HourglassEmpty as WaitingIcon,
  PlayArrow as InProgressIcon,
  Cancel as NoShowIcon,
} from '@mui/icons-material';
import {
  ReceptionSummary,
  AttendanceStatus,
  ATTENDANCE_STATUS_LABELS,
} from '../../../types/reception';

interface ReceptionSummaryCardsProps {
  summary: ReceptionSummary | null;
  loading?: boolean;
  onStatusClick?: (status: AttendanceStatus) => void;
  selectedStatuses?: AttendanceStatus[];
}

const StatusChip: React.FC<{
  status: AttendanceStatus;
  count: number;
  loading?: boolean;
  onClick?: () => void;
  selected?: boolean;
}> = ({ status, count, loading, onClick, selected }) => {
  const colorMap: Record<AttendanceStatus, string> = {
    [AttendanceStatus.NOT_ATTENDED]: '#9e9e9e',
    [AttendanceStatus.CHECKED_IN]: '#2196f3',
    [AttendanceStatus.TRIAGED]: '#9c27b0',
    [AttendanceStatus.IN_PROGRESS]: '#ff9800',
    [AttendanceStatus.COMPLETED]: '#4caf50',
    [AttendanceStatus.NO_SHOW]: '#f44336',
  };

  const iconMap: Record<AttendanceStatus, React.ReactNode> = {
    [AttendanceStatus.NOT_ATTENDED]: <WaitingIcon sx={{ fontSize: 16 }} />,
    [AttendanceStatus.CHECKED_IN]: <CheckIcon sx={{ fontSize: 16 }} />,
    [AttendanceStatus.TRIAGED]: <CheckIcon sx={{ fontSize: 16 }} />,
    [AttendanceStatus.IN_PROGRESS]: <InProgressIcon sx={{ fontSize: 16 }} />,
    [AttendanceStatus.COMPLETED]: <CheckIcon sx={{ fontSize: 16 }} />,
    [AttendanceStatus.NO_SHOW]: <NoShowIcon sx={{ fontSize: 16 }} />,
  };

  const chip = (
    <Chip
      icon={iconMap[status] as React.ReactElement}
      label={
        loading ? (
          <Skeleton width={40} />
        ) : (
          `${ATTENDANCE_STATUS_LABELS[status]}: ${count}`
        )
      }
      size="small"
      sx={{
        transition: 'none',
        backgroundColor: selected ? `${colorMap[status]}35` : `${colorMap[status]}20`,
        color: colorMap[status],
        border: selected ? `2px solid ${colorMap[status]}` : '2px solid transparent',
        fontWeight: selected ? 700 : 400,
        '& .MuiChip-icon': {
          color: colorMap[status],
        },
      }}
    />
  );

  if (!onClick) return chip;

  return (
    <Box
      onClick={onClick}
      sx={{ cursor: 'pointer', display: 'inline-flex' }}
    >
      {chip}
    </Box>
  );
};

const InlineMetric: React.FC<{ label: string; value: number; color: string; loading?: boolean }> = ({
  label,
  value,
  color,
  loading,
}) => (
  <Typography variant="body2" component="span" color="text.secondary">
    {label}:{' '}
    {loading ? (
      <Skeleton width={20} sx={{ display: 'inline-block' }} />
    ) : (
      <Typography variant="body2" component="span" fontWeight="bold" color={color}>
        {value}
      </Typography>
    )}
  </Typography>
);

export const ReceptionSummaryCards: React.FC<ReceptionSummaryCardsProps> = ({
  summary,
  loading = false,
  onStatusClick,
  selectedStatuses = [],
}) => {
  const byStatus = summary?.byStatus ?? {};

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        {/* Left: inline metrics */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <InlineMetric label="Total" value={summary?.total || 0} color="#1976d2" loading={loading} />
          <Typography variant="body2" color="text.disabled">·</Typography>
          <InlineMetric label="Manhã" value={summary?.morning || 0} color="#ff9800" loading={loading} />
          <Typography variant="body2" color="text.disabled">·</Typography>
          <InlineMetric label="Tarde" value={summary?.afternoon || 0} color="#673ab7" loading={loading} />
        </Stack>

        {/* Right: status chips */}
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {Object.values(AttendanceStatus).map(status => (
            <StatusChip
              key={status}
              status={status}
              count={byStatus[status] || 0}
              loading={loading}
              onClick={onStatusClick ? () => onStatusClick(status) : undefined}
              selected={selectedStatuses.includes(status)}
            />
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};

export default ReceptionSummaryCards;
